import { useEffect, useState } from 'react';
import './App.css';
import { BrowserRouter, Navigate, Routes, Route, useLocation } from 'react-router-dom';
import Home from './pages/Home/Home';
import Solutions from './pages/Solutions/Solutions';
import About from './pages/About/About';
import Contact from './pages/Contact/Contact';
import Partnership from './pages/Partnership/Partnership';
import Privacy from './pages/Privacy/Privacy';
import SolutionDetails from './pages/SolutionDetails/SolutionDetails';
import WealthAndSavings from './pages/WealthAndSavings/WealthAndSavings';
import WealthAndSavingsDetails from './pages/WealthAndSavingsDetails/WealthAndSavingsDetails';
import MarketDetails from './pages/MarketDetails/MarketDetails';
import CreateAccount from './pages/CreateAccount/CreateAccount';
import Technology from './pages/Technology/Technology';
import Infrastructure from './pages/Infrastructure/Infrastructure';
import Platforms from './pages/Platforms/Platforms';
import VPSHosting from './pages/VPSHosting/VPSHosting';
import VPSHostingRequest from './pages/VPSHostingRequest/VPSHostingRequest';
import Insights from './pages/Insights/Insights';
import EconomicCalendar from './pages/EconomicCalendar/EconomicCalendar';
import MarketAnalysis from './pages/MarketAnalysis/MarketAnalysis';
import MarketAnalysisDetails from './pages/MarketAnalysisDetails/MarketAnalysisDetails';
import LoginPage from './pages/LoginPage';
import DashboardPage from './pages/DashboardPage';
import GlobalLayoutPage from './pages/GlobalLayoutPage';
import PagesPage from './pages/PagesPage';
import PageEditorPage from './pages/PageEditorPage';
import SolutionDetailPagesPage from './pages/SolutionDetailPagesPage';
import MarketDetailPagesPage from './pages/MarketDetailPagesPage';
import WealthDetailPagesPage from './pages/WealthDetailPagesPage';
import MarketAnalysisPostPagesPage from './pages/MarketAnalysisPostPagesPage';
import AccountSubmissionsPage from './pages/AccountSubmissionsPage';
import AccountSubmissionDetailPage from './pages/AccountSubmissionDetailPage';
import ContactSubmissionsPage from './pages/ContactSubmissionsPage';
import ContactSubmissionDetailPage from './pages/ContactSubmissionDetailPage';
import PartnershipSubmissionsPage from './pages/PartnershipSubmissionsPage';
import PartnershipSubmissionDetailPage from './pages/PartnershipSubmissionDetailPage';
import VpsHostingRequestsPage from './pages/VpsHostingRequestsPage';
import VpsHostingRequestDetailPage from './pages/VpsHostingRequestDetailPage';
import NewsletterSubscriptionsPage from './pages/NewsletterSubscriptionsPage';
import DashboardLayout from './components/dashboard/DashboardLayout';
import Loader from './components/shared/Loader/Loader';
import ScrollToTop from './components/shared/ScrollToTop/ScrollToTop';
import PublicLayout from './layouts/PublicLayout';
import { AuthProvider } from './context/AuthContext';
import ProtectedRoute from './routes/ProtectedRoute';
import { lockBodyScroll, unlockBodyScroll } from './utils/bodyScrollLock';

const isAdminPath = (pathname) =>
  pathname.startsWith('/login') || pathname.startsWith('/dashboard');

function AppShell() {
  const location = useLocation();
  const [isLoading, setIsLoading] = useState(
    () => !isAdminPath(window.location.pathname)
  );

  useEffect(() => {
    if (isAdminPath(location.pathname)) {
      setIsLoading(false);
      return undefined;
    }

    if (!isLoading) {
      return undefined;
    }

    lockBodyScroll();

    const timer = setTimeout(() => {
      setIsLoading(false);
      unlockBodyScroll();
      window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
    }, 1100);

    return () => {
      clearTimeout(timer);
      unlockBodyScroll();
    };
  }, [isLoading, location.pathname]);

  return (
    <>
      {isLoading && <Loader />}
      <ScrollToTop />
      <Routes>
        <Route element={<PublicLayout />}>
          <Route path="/" element={<Home />} />
          <Route path="/solutions" element={<Solutions />} />
          <Route path="/solutions/:slug" element={<SolutionDetails />} />
          <Route path="/about" element={<About />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/partnership" element={<Partnership />} />
          <Route path="/privacy" element={<Privacy />} />
          <Route path="/wealth-savings" element={<WealthAndSavings />} />
          <Route path="/wealth" element={<WealthAndSavings />} />
          <Route path="/wealth/:slug" element={<WealthAndSavingsDetails />} />
          <Route path="/markets" element={<Navigate to="/markets/equities" replace />} />
          <Route path="/markets/:slug" element={<MarketDetails />} />
          <Route path="/create-account" element={<CreateAccount />} />
          <Route path="/technology" element={<Technology />} />
          <Route path="/technology/infrastructure" element={<Infrastructure />} />
          <Route path="/technology/platforms" element={<Platforms />} />
          <Route path="/technology/vps-hosting" element={<VPSHosting />} />
          <Route
            path="/technology/vps-hosting/request"
            element={<VPSHostingRequest />}
          />
          <Route path="/insights" element={<Insights />} />
          <Route path="/insights/economic-calendar" element={<EconomicCalendar />} />
          <Route path="/insights/market-analysis" element={<MarketAnalysis />} />
          <Route
            path="/insights/market-analysis/:slug"
            element={<MarketAnalysisDetails />}
          />
        </Route>

        <Route path="/login" element={<LoginPage />} />
        <Route
          path="/dashboard"
          element={
            <ProtectedRoute>
              <DashboardLayout />
            </ProtectedRoute>
          }
        >
          <Route index element={<DashboardPage />} />
          <Route path="global-layout" element={<GlobalLayoutPage />} />
          <Route path="pages" element={<PagesPage />} />
          <Route path="pages/solution-details" element={<SolutionDetailPagesPage />} />
          <Route path="pages/market-details" element={<MarketDetailPagesPage />} />
          <Route path="pages/wealth-details" element={<WealthDetailPagesPage />} />
          <Route path="pages/market-analysis-posts" element={<MarketAnalysisPostPagesPage />} />
          <Route path="pages/:slug" element={<PageEditorPage />} />
          <Route path="account-submissions" element={<AccountSubmissionsPage />} />
          <Route path="account-submissions/:id" element={<AccountSubmissionDetailPage />} />
          <Route path="contact-submissions" element={<ContactSubmissionsPage />} />
          <Route path="contact-submissions/:id" element={<ContactSubmissionDetailPage />} />
          <Route path="partnership-submissions" element={<PartnershipSubmissionsPage />} />
          <Route path="partnership-submissions/:id" element={<PartnershipSubmissionDetailPage />} />
          <Route path="vps-hosting-requests" element={<VpsHostingRequestsPage />} />
          <Route path="vps-hosting-requests/:id" element={<VpsHostingRequestDetailPage />} />
          <Route path="newsletter-subscriptions" element={<NewsletterSubscriptionsPage />} />
        </Route>
      </Routes>
    </>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppShell />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
